/**
 * ISSUES — every problem this till reported, with its Issue Console ticket.
 *
 * The device-health worker files each problem into the Issue Console (one
 * ticket per problem per store; this till is listed inside it with its own
 * count), and `/device-health/problems?deviceId=` already returns the ticket
 * id, its live status and priority. This tab shows them and links each one to
 * `/app-issues/<id>/console`. A problem whose ticket write has not landed yet
 * has `issueId: null` and says so — it is still a live problem.
 *
 * Status words and colours come from `fleetStatus.ts`, the same vocabulary
 * the Fleet Monitoring list and the Issue Console queue use.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import type { DeviceHealthProblem } from '@platform/shared-types';
import { deviceHealthApi } from '../../../services/deviceHealthApi';
import { RubyLoader } from '../../../components/RubyLoader';
import {
  ISSUE_STATUS_LABEL, ISSUE_STATUS_TONE, TONE_PILL, issueHref,
} from '../fleetStatus';
import { formatWhen, relativeTime, type DeviceTabProps } from '../../pos/device/deviceDetail';

export type IssueFilter = 'open' | 'resolved' | 'all';

const FILTERS: { id: IssueFilter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all', label: 'All' },
];

const KIND_LABEL: Record<string, string> = {
  'crash.native': 'Crash', 'crash.exit': 'Crash', 'crash.renderer': 'Crash',
  'js.error': 'App error', 'js.rejection': 'App error',
  'net.fail': 'Server', 'print.fail': 'Printer', 'usb.drop': 'USB', 'pay.anomaly': 'Payment',
  warn: 'Warning', 'session.start': 'Session',
};

const PRIORITY_CLASS: Record<string, string> = {
  critical: 'rounded bg-[#DC143C] px-1.5 py-px text-white',
  high: 'text-[#B91C1C]',
  medium: 'text-amber-700',
  low: 'text-stone-500',
};

/** `#7f3a91c2` — the last 8 characters, enough to say on a call. */
export const shortTicketId = (issueId: string) => `#${issueId.slice(-8)}`;

/** One problem row — rendered by this tab and by Overview's "Reported issues" card. */
export function IssueRow({ problem: p }: { problem: DeviceHealthProblem }) {
  const status = p.ticketStatus ?? (p.issueId ? 'new' : null);
  const tone = status ? ISSUE_STATUS_TONE[status] || 'mute' : 'mute';
  const here = p.deviceCount ?? p.count;
  const last = p.deviceLastSeen ?? p.lastSeen;
  const version = p.versions?.length ? p.versions[p.versions.length - 1] : null;
  return (
    <li
      data-testid="issue-row"
      className="grid items-center gap-3 border-b border-stone-200 px-4 py-3 last:border-b-0 md:grid-cols-[minmax(0,2.4fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
    >
      <div className="min-w-0">
        <div className="font-semibold text-stone-900">{p.title}</div>
        <div className="mt-0.5 text-[12px] text-stone-500">
          {p.issueId && <span className="font-mono text-[11.5px]">{shortTicketId(p.issueId)} · </span>}
          {KIND_LABEL[p.kind] || p.kind}
          {version && <> · version <span className="font-mono">{version}</span>{p.versions.length > 1 ? ` (+${p.versions.length - 1} more)` : ''}</>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:grid md:justify-items-start md:gap-1">
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold ${TONE_PILL[tone]}`}>
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
          {status ? ISSUE_STATUS_LABEL[status] || status : 'Filing…'}
        </span>
        {p.ticketPriority && (
          <span className={`text-[11px] font-bold uppercase tracking-wide ${PRIORITY_CLASS[p.ticketPriority] || 'text-stone-500'}`}>
            {p.ticketPriority}
          </span>
        )}
      </div>
      <div className="text-[12px] text-stone-500">
        <b className="font-semibold text-stone-900 tabular-nums">{here}×</b> on this till
        {p.devices > 1 && <div>{p.count}× on {p.devices} tills</div>}
      </div>
      <div className="text-[12px] text-stone-500">
        <div>First {formatWhen(p.firstSeen) || '—'}</div>
        <div>Last {relativeTime(last) || formatWhen(last) || '—'}</div>
      </div>
      <div>
        {p.issueId ? (
          <Link
            to={issueHref(p.issueId)}
            className="inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-xl border border-stone-300 bg-white px-3 text-[13px] font-semibold text-[#DC143C] hover:border-[#DC143C] hover:bg-[#FFF1F3]"
          >
            Open ticket <ExternalLink size={14} aria-hidden="true" />
          </Link>
        ) : (
          <span className="inline-flex min-h-[44px] items-center text-[12.5px] text-stone-500">Ticket being filed…</span>
        )}
      </div>
    </li>
  );
}

export default function IssuesTab({ row }: DeviceTabProps) {
  const deviceId = row.deviceId;
  const [filter, setFilter] = useState<IssueFilter>('open');
  const [problems, setProblems] = useState<DeviceHealthProblem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!deviceId) { setProblems([]); return; }
    let live = true;
    setProblems(null);
    setError(null);
    deviceHealthApi.problems({ deviceId, status: filter, window: '7d' })
      .then((list) => { if (live) setProblems(list || []); })
      .catch((e: any) => { if (live) setError(e?.message || 'Could not load issues'); });
    return () => { live = false; };
  }, [deviceId, filter]);

  return (
    <section className="rounded-2xl border border-stone-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 px-4 py-3">
        <h2 className="text-[14px] font-semibold text-stone-900">Issues reported by this till</h2>
        <div role="group" aria-label="Issue filter" className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const on = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(f.id)}
                className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-3.5 text-[13px] ${
                  on ? 'border-[#DC143C] bg-[#DC143C] font-semibold text-white' : 'border-stone-300 bg-white text-stone-700 hover:border-stone-400'
                }`}
              >
                {f.label}
                {on && problems && <span className="tabular-nums text-rose-100">{problems.length}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {error ? (
        <p role="alert" className="m-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12.5px] text-rose-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          Could not load issues — {error}
        </p>
      ) : problems === null ? (
        <RubyLoader variant="section" label="Loading issues…" />
      ) : problems.length === 0 ? (
        <p className="px-5 py-10 text-center text-[13px] text-stone-500">
          {filter === 'open'
            ? 'No open issues. Anything this till reports is filed to the Issue Console automatically and shows here.'
            : filter === 'resolved' ? 'No resolved issues in the last 7 days.' : 'Nothing reported in the last 7 days.'}
        </p>
      ) : (
        <ul>{problems.map((p) => <IssueRow key={`${p.fingerprint}-${p.tenantId}`} problem={p} />)}</ul>
      )}

      <p className="border-t border-stone-100 px-4 py-2 text-[11.5px] text-stone-400">
        Covers the last 7 days. A resolved ticket that happens again reopens by itself.
      </p>
    </section>
  );
}
